import React, { useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Menu,
  MenuItem,
  Avatar,
  useTheme,
  useMediaQuery,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  ExitToApp as LogoutIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { navigationConfig } from '../../config/navigation';
import Logo from '../../components/common/Logo';
import { useLocation } from 'react-router-dom';

const drawerWidth = 240;

const Layout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [ mobileOpen, setMobileOpen ] = useState( false );
  const [ profileAnchorEl, setProfileAnchorEl ] = useState<null | HTMLElement>( null );
  const [ logoutDialogOpen, setLogoutDialogOpen ] = useState( false );
  const theme = useTheme();
  const isMobile = useMediaQuery( theme.breakpoints.down( 'sm' ) );
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen( !mobileOpen );
  };

  const handleProfileClick = ( event: React.MouseEvent<HTMLElement> ) => {
    setProfileAnchorEl( event.currentTarget );
  };

  const handleProfileClose = () => {
    setProfileAnchorEl( null );
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen( true );
    handleProfileClose();
  };

  const handleLogoutConfirm = () => {
    logout();
    setLogoutDialogOpen( false );
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen( false );
  };

  const drawer = (
    <div>
      <Toolbar>
        <Logo size="small" />

      </Toolbar>
      <Divider />
      <List>
        { navigationConfig.map( ( item ) => {
          const isActive = location.pathname === item.path;

          return (
            <ListItem
              button
              key={ item.title }
              selected={ isActive }   // ✅ ACTIVE TRUE
              onClick={ () => {
                navigate( item.path );
                if ( isMobile ) setMobileOpen( false );
              } }
              sx={ {
                borderRadius: 2,
                mx: 1,
                mb: 0.5,
                backgroundColor: isActive ? 'primary.main' : 'transparent',
                color: isActive ? 'black' : 'inherit',
                '&:hover': {
                  backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                },
                '& .MuiListItemIcon-root': {
                  color: isActive ? 'black' : 'inherit',
                },
              } }
            >
              <ListItemIcon>{ item.icon }</ListItemIcon>
              <ListItemText primary={ t( item.title ) } />
            </ListItem>
          );
        } ) }
      </List>

    </div>
  );

  return (
    <Box sx={ { display: 'flex' } }>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={ {
          width: { sm: `calc(100% - ${ drawerWidth }px)` },
          ml: { sm: `${ drawerWidth }px` },
        } }
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={ handleDrawerToggle }
            sx={ { mr: 2, display: { sm: 'none' } } }
          >
            <MenuIcon />
          </IconButton>
          <Box sx={ { flexGrow: 1 } } />
          { user && (
            <Box sx={ { display: 'flex', alignItems: 'center', mr: 2 } }>
              <Box sx={ { textAlign: 'right', mr: 1 } }>
                <Typography variant="body2" sx={ { color: 'inherit', fontWeight: 'medium' } }>
                  { user.name }
                </Typography>
                <Typography variant="caption" sx={ { color: 'inherit', opacity: 0.8 } }>
                  { user.email }
                </Typography>
              </Box>
            </Box>
          ) }
          <IconButton
            color="inherit"
            onClick={ handleProfileClick }
          >
            <Avatar sx={ { width: 32, height: 32 } }>
              <PersonIcon />
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={ profileAnchorEl }
            open={ Boolean( profileAnchorEl ) }
            onClose={ handleProfileClose }
            PaperProps={ {
              sx: {
                minWidth: 250,
                mt: 1
              }
            } }
          >
            {/* {user && (
              <>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {user.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
                <Divider />
              </>
            )} */}
            {/* <MenuItem onClick={() => { navigate('/profile'); handleProfileClose(); }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t('profile.title')}
            </MenuItem> */}
            <MenuItem onClick={ () => { navigate( '/change-password' ); handleProfileClose(); } }>
              <ListItemIcon>
                <LockIcon fontSize="small" />
              </ListItemIcon>
              { t( 'auth.changePassword.title' ) }
            </MenuItem>
            <Divider />
            <MenuItem onClick={ handleLogoutClick }>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              { t( 'navigation.logout' ) }
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={ { width: { sm: drawerWidth }, flexShrink: { sm: 0 } } }
      >
        <Drawer
          variant={ isMobile ? 'temporary' : 'permanent' }
          open={ isMobile ? mobileOpen : true }
          onClose={ handleDrawerToggle }
          ModalProps={ {
            keepMounted: true,
          } }
          sx={ {
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          } }
        >
          { drawer }
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={ {
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${ drawerWidth }px)` },
          mt: '64px'
        } }
      >
        <Outlet />
      </Box>

      {/* Logout Confirmation Dialog */ }
      <Dialog
        open={ logoutDialogOpen }
        onClose={ handleLogoutCancel }
        maxWidth="sm"
        fullWidth
      >
        {/* 🔵 Header Section */ }
        <DialogTitle
          sx={ {
            bgcolor: "primary.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 2.5,
            px: 3,
            fontWeight: "bold",
            fontSize: "1.25rem"
          } }
        >
          Confirm Logout
        </DialogTitle>

        {/* 📝 Message */ }
        <DialogContent sx={ { paddingTop: "20px" } }>
          <Typography sx={ { fontSize: "1rem", paddingTop: "20px" } }>
            Are you sure you want to logout?
          </Typography>
        </DialogContent>

        {/* 🔘 Buttons */ }
        <DialogActions sx={ { p: 3, gap: 2 } }>
          <Button
            onClick={ handleLogoutCancel }
            variant="outlined"
            sx={ {
              minWidth: 100,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600
            } }
          >
            Cancel
          </Button>

          <Button
            onClick={ handleLogoutConfirm }
            variant="contained"
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "primary.main",
              "&:hover": { bgcolor: "primary.dark" }
            } }
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Layout;
